import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    IconButton,
    Stack,
    LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface VideoLiveRecorderProps {
    /** Maximum recording duration in seconds. When elapsed, recording stops automatically. */
    maxDurationSeconds: number;
    /** Whether this is a read-only preview (config mode) vs. actual recording */
    previewOnly?: boolean;
    /** Called with the recorded File blob when recording completes. */
    onRecordingComplete?: (file: File) => void;
}

const VideoPreview = styled('video')(({ theme }) => ({
    width: '100%',
    maxHeight: 400,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[900],
}));

const RecDot = styled('span')(({ theme }) => ({
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    display: 'inline-block',
    animation: 'pulse 1.2s infinite',
    '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.3 },
    },
}));

const VideoLiveRecorder: React.FC<VideoLiveRecorderProps> = ({
    maxDurationSeconds,
    previewOnly = false,
    onRecordingComplete,
}) => {
    const { showSnackbar } = useSnackbar();

    const [stream, setStream] = useState<MediaStream | null>(null);
    const [permissionState, setPermissionState] = useState<
        'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported'
    >('idle');
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const autoStopRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopAllTracks();
            if (timerRef.current) clearInterval(timerRef.current);
            if (autoStopRef.current) clearTimeout(autoStopRef.current);
            if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordedBlobUrl]);

    const stopAllTracks = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }, [stream]);

    const requestCamera = useCallback(async () => {
        // Check browser support
        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setPermissionState('unsupported');
            showSnackbar("Votre navigateur ne supporte pas l'enregistrement vidéo", 'warning');
            return;
        }

        setPermissionState('requesting');
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
                audio: true,
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
            setPermissionState('granted');
        } catch (err: any) {
            setPermissionState('denied');
            showSnackbar(
                'Accès à la caméra refusé. Veuillez autoriser la caméra dans les paramètres du navigateur.',
                'error',
            );
        }
    }, [showSnackbar]);

    const startRecording = useCallback(() => {
        if (!stream) return;

        chunksRef.current = [];
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8,opus' });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'video/webm' });
            setRecordedBlob(blob);
            const url = URL.createObjectURL(blob);
            setRecordedBlobUrl(url);

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            if (timerRef.current) clearInterval(timerRef.current);
            if (autoStopRef.current) clearTimeout(autoStopRef.current);
        };

        recorder.start(500); // dataavailable every 500ms
        setRecording(true);
        setElapsed(0);
        setRecordedBlobUrl(null);
        setRecordedBlob(null);

        // Timer
        timerRef.current = setInterval(() => {
            setElapsed(prev => prev + 1);
        }, 1000);

        // Auto-stop at max duration
        autoStopRef.current = setTimeout(() => {
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
                setRecording(false);
            }
        }, maxDurationSeconds * 1000);

        // Switch video to self-view (so user doesn't see themselves in loop)
        // Actually, we want to keep the live preview - mediaRecorder takes from the stream.
    }, [stream, maxDurationSeconds]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
        if (autoStopRef.current) clearTimeout(autoStopRef.current);
    }, []);

    const handleStartStop = () => {
        if (recording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const handleRetake = () => {
        if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
        setRecordedBlobUrl(null);
        setRecordedBlob(null);
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    };

    const handleUpload = () => {
        if (recordedBlob && onRecordingComplete) {
            // Append webm extension for the server
            const fileName = `recording-${Date.now()}.webm`;
            const file = new File([recordedBlob], fileName, { type: 'video/webm' });
            onRecordingComplete(file);
            showSnackbar('Vidéo prête à être envoyée', 'success');
        }
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const progress = maxDurationSeconds > 0 ? (elapsed / maxDurationSeconds) * 100 : 0;

    // Preview-only mode (shown in the QuestionEditor config panel)
    if (previewOnly) {
        return (
            <Box
                sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                    🎥 Enregistrement vidéo en direct
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    Durée max : {formatTime(maxDurationSeconds)}
                </Typography>
            </Box>
        );
    }

    // Unsupported browser
    if (permissionState === 'unsupported') {
        return (
            <Box textAlign="center" py={4}>
                <Typography color="error" variant="body2">
                    Votre navigateur ne supporte pas l'enregistrement vidéo.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Video preview */}
            <Box position="relative" mb={1}>
                <VideoPreview
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    src={recordedBlobUrl ?? undefined}
                />

                {/* Recording indicator */}
                {recording && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                        }}>
                        <RecDot />
                        <Typography variant="caption" color="white">
                            {formatTime(elapsed)} / {formatTime(maxDurationSeconds)}
                        </Typography>
                    </Stack>
                )}

                {/* Progress bar */}
                {recording && (
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            backgroundColor: 'transparent',
                        }}
                    />
                )}
            </Box>

            {/* Controls */}
            <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                {/* Camera not started yet */}
                {permissionState !== 'granted' && (
                    <Button
                        variant="outlined"
                        onClick={requestCamera}
                        disabled={permissionState === 'requesting'}
                        startIcon={
                            permissionState === 'requesting' ? <CircularProgress size={16} /> : null
                        }>
                        Autoriser la caméra
                    </Button>
                )}

                {/* Camera ready, not recording and no recording yet */}
                {permissionState === 'granted' && !recording && !recordedBlobUrl && (
                    <Button variant="contained" color="error" onClick={handleStartStop}>
                        ⏺ Enregistrer
                    </Button>
                )}

                {/* Recording in progress */}
                {recording && (
                    <Button variant="contained" color="primary" onClick={handleStartStop}>
                        ⏹ Arrêter
                    </Button>
                )}

                {/* Recording done */}
                {recordedBlobUrl && !recording && (
                    <>
                        <Button variant="outlined" onClick={handleRetake}>
                            🔄 Recommencer
                        </Button>
                        <Button variant="contained" onClick={handleUpload}>
                            ✅ Utiliser cette vidéo
                        </Button>
                    </>
                )}
            </Stack>
        </Box>
    );
};

export default VideoLiveRecorder;
